import { HttpClient, HttpContext } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, switchMap, interval, takeWhile, of } from 'rxjs';
import { AppConfigService } from '../config.service';
import { IPaymentGateway } from './payment-gateway.interface';
import { PaymentRequest, PaymentStatusResponse } from '@core/models/payment';
import { SKIP_ERROR_MODAL } from '@core/interceptors/error.interceptor';

/**
 * Stripe payment gateway implementation
 */
@Injectable()
export class StripePaymentGateway extends IPaymentGateway {
  private readonly stripeApiHost = 'https://pay.vivenu.dev/api/';
  private readonly http = inject(HttpClient);
  private readonly appConfigService = inject(AppConfigService);

  //create payment request
  createPaymentRequest(request: PaymentRequest): Observable<any> {
    return this.http.post(
      `${this.appConfigService.host()}/payments/requests`,
      request
    );
  }

  //set payment processor
  setPaymentProcessor(requestId: string, gatewayId: string): Observable<any> {
    return this.http.post(
      `${this.appConfigService.host()}/payments/requests/${requestId}/processor`,
      { paymentGatewayId: gatewayId }
    );
  }

  //trigger the payment in the terminal
  sendPaymentToTerminal(requestId: string, gatewayId: string): Observable<any> {
    const terminalId = this.appConfigService.currentConfig().terminalId;
    const dto = {
      paymentGatewayId: gatewayId,
      paymentRequestId: requestId,
      paymentGatewayType: 'stripe',
      terminalId: terminalId,
      mode: 'card',
    };

    //use HttpContext to skip the error modal for terminal errors (terminal offline - Api returns 500 error)
    return this.http.post<{id: string, status: string}>(
      `${this.stripeApiHost}/payments/terminals/process`, 
      dto,
      { context: new HttpContext().set(SKIP_ERROR_MODAL, true) }
    );
  }

  //poll the payment status (each 1000ms)
  checkPaymentStatus(requestId: string, gatewayId: string, terminalId?: string, processorId?: string): Observable<PaymentStatusResponse> {
    const endpoint = `${this.stripeApiHost}/payments/terminals/process/${processorId}?paymentGatewayId=${gatewayId}&paymentGatewayType=stripe`;
    const verifyProcessor$ = this.http.post<PaymentStatusResponse>(`${this.appConfigService.host()}/payments/requests/${requestId}/verify`, {
      paymentGatewayId: gatewayId,
    });
    return interval(1000).pipe(
      switchMap(() => this.http.get<PaymentStatusResponse>(endpoint)),
      takeWhile((res: PaymentStatusResponse) => res.status === 'PENDING', true),
      switchMap((res: PaymentStatusResponse) => 
        res.status === 'SUCCEEDED' ? verifyProcessor$ : of(res)
      )
    );
  }

  //cancel the payment
  cancelPayment(gatewayId: string, processorId?: string): Observable<any> {
    const dto = { id: processorId };
    return this.http.post(`${this.stripeApiHost}/payments/terminals/process/${processorId}/cancel?paymentGatewayId=${gatewayId}&paymentGatewayType=stripe`, dto)
  }
}

